import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Empty, Form, Modal, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { addValidationOnError } from '../../util'
import { useMessageTemplates } from '../../campaign/message-template-hooks'
import { useTemplateLanguageNotice } from '../../campaign/template-language-notice'
import { PhoneNumberSelect } from '../../campaign/components/create/steps/phone-number-select'
import { TemplateSelect } from '../../campaign/components/create/steps/template-select'
import { WhatsappBusinessAccountSelect } from '../../campaign/components/create/steps/whatsapp-business-account-select'
import { usePhoneNumbers } from '../../whatsapp-business-account/phone-number-hooks'
import { WhatsappInput } from '../api'
import { useApplyStartMessage, useSite, useUpdateWhatsapp, useWhatsapp } from '../hooks'
import { WhatsappSettings } from '../site'

/** The site's language, in a hidden field: the template select and its language notice read it from the form. */
type FormValues = WhatsappInput & { locale?: string }

/**
 * The WhatsApp defaults for vacancies where applying via WhatsApp is switched on. Vacancies that already have it keep
 * their own settings; only the start message can be given to them afterwards (a new number would split the
 * conversations candidates already have). Uses the same fields as the WhatsApp settings of a campaign.
 */
export function SiteWhatsapp({ siteId }: { siteId: string }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data: site } = useSite(siteId)
    const { data, isLoading } = useWhatsapp(siteId)
    const update = useUpdateWhatsapp(siteId)
    const apply = useApplyStartMessage(siteId)
    const [form] = Form.useForm<FormValues>()
    const [askApply, setAskApply] = useState(false)
    const vacancies = data?.screenings ?? 0
    const canUpdate = can(UPDATE_SITE)

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                whatsappBusinessAccountId: data.selected.whatsappBusinessAccountId ?? undefined,
                phoneNumberId: data.selected.phoneNumberId ?? undefined,
                startMessageTemplateId: data.selected.startMessageTemplateId ?? undefined,
                locale: site?.locale,
            })
        }
    }, [data, site, form])

    const save = (formValues: FormValues) => {
        // The language is only there for the template select.
        const values: WhatsappInput = {
            whatsappBusinessAccountId: formValues.whatsappBusinessAccountId,
            phoneNumberId: formValues.phoneNumberId,
            startMessageTemplateId: formValues.startMessageTemplateId,
        }

        const startMessageChanged = values.startMessageTemplateId !== data?.selected.startMessageTemplateId

        return update
            .mutateAsync(values)
            .then((next) => {
                if (startMessageChanged && (next.screenings ?? 0) > 0) {
                    setAskApply(true)
                }
            })
            .catch(addValidationOnError(form))
    }

    const renderDefaults = () => {
        if (isLoading || !data) {
            return <Skeleton active />
        }

        if (data.accounts.length === 0) {
            return <Empty description={t('Connect a WhatsApp business account first.')} />
        }

        return (
            <Form
                form={form}
                layout="vertical"
                className="max-w-lg"
                disabled={!canUpdate}
                onFinish={save}
            >
                <Form.Item
                    name="locale"
                    hidden
                />
                <WhatsappFields
                    settings={data}
                    hasVacancies={vacancies > 0}
                />
                {canUpdate && (
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={update.isPending}
                    >
                        {t('Save')}
                    </Button>
                )}
            </Form>
        )
    }

    return (
        <Card title={t('Defaults for new vacancies')}>
            <Typography.Paragraph type="secondary">
                {t(
                    'Vacancies where you switch on applying via WhatsApp use this number and start message. Vacancies that already have it keep their own settings. You switch it on per vacancy under Vacancies.'
                )}
            </Typography.Paragraph>
            {data && vacancies > 0 && (
                <StartMessageNotice
                    vacancies={vacancies}
                    differentStartMessage={data.differentStartMessage}
                    canUpdate={canUpdate}
                    onUseForAll={() => setAskApply(true)}
                />
            )}
            {renderDefaults()}
            <Modal
                open={askApply}
                title={t('Also use it for vacancies that already apply via WhatsApp?')}
                okText={t('Use for {{count}} vacancies', { count: vacancies })}
                cancelText={t('Only new vacancies')}
                confirmLoading={apply.isPending}
                onCancel={() => setAskApply(false)}
                onOk={() => apply.mutateAsync().then(() => setAskApply(false))}
            >
                <Typography.Paragraph>
                    {t(
                        'Those vacancies then send this start message to new candidates. Candidates who already got a message are not affected. Vacancies on another WhatsApp account keep their start message.'
                    )}
                </Typography.Paragraph>
            </Modal>
        </Card>
    )
}

type StartMessageNoticeProps = {
    vacancies: number
    differentStartMessage: WhatsappSettings['differentStartMessage']
    canUpdate: boolean
    onUseForAll: () => void
}

/**
 * Whether the vacancies that already apply via WhatsApp use the default start message.
 */
function StartMessageNotice({ vacancies, differentStartMessage, canUpdate, onUseForAll }: StartMessageNoticeProps) {
    const { t } = useTranslation()

    if (!differentStartMessage) {
        return (
            <Alert
                className="mb-4"
                type="success"
                showIcon
                message={t('All {{count}} vacancies use this start message', { count: vacancies })}
            />
        )
    }

    return (
        <Alert
            className="mb-4"
            type="info"
            showIcon
            message={t('{{count}} of {{total}} vacancies use another start message', {
                count: differentStartMessage,
                total: vacancies,
            })}
            action={
                canUpdate ? (
                    <Button
                        size="small"
                        onClick={onUseForAll}
                    >
                        {t('Use for all')}
                    </Button>
                ) : undefined
            }
        />
    )
}

type WhatsappFieldsProps = {
    settings: WhatsappSettings
    hasVacancies: boolean
}

/**
 * Inside the form, because the template select and its language notice read the language from it.
 */
function WhatsappFields({ settings, hasVacancies }: WhatsappFieldsProps) {
    const { t } = useTranslation()
    const form = Form.useFormInstance<FormValues>()
    const accountId = Form.useWatch('whatsappBusinessAccountId', form)
    const startId = Form.useWatch('startMessageTemplateId', form)
    const phoneNumbers = usePhoneNumbers(accountId)
    const templates = useMessageTemplates({
        enabled: Boolean(accountId),
        preservePreviousData: false,
        query: {
            size: 100,
            sort: '-created_at',
            filters: {
                purpose: ['start'],
                whatsappBusinessAccountId: accountId ? [accountId] : undefined,
            },
        },
    })

    // Only the start messages the website may use (approved, of this company), as the server checks too.
    const account = settings.accounts.find((option) => option.id === accountId)
    const allowed = new Set(account?.startTemplates.map((template) => template.id))

    const startTemplates = (templates.data?.data ?? []).filter((template) => allowed.has(template.id))
    const languageNotice = useTemplateLanguageNotice(startTemplates.find((template) => template.id === startId))

    return (
        <>
            <Form.Item
                name="whatsappBusinessAccountId"
                label={t('WhatsApp account')}
                rules={[{ required: true }]}
            >
                <WhatsappBusinessAccountSelect
                    accounts={settings.accounts}
                    onChange={() =>
                        form.setFieldsValue({
                            phoneNumberId: undefined,
                            startMessageTemplateId: undefined,
                        })
                    }
                />
            </Form.Item>
            <Form.Item
                name="phoneNumberId"
                label={t('Phone number')}
                rules={[{ required: true }]}
                extra={hasVacancies ? t('A new number only applies to vacancies you switch on after this.') : undefined}
            >
                <PhoneNumberSelect
                    phoneNumbers={phoneNumbers.data?.data ?? []}
                    loading={phoneNumbers.isLoading}
                    disabled={!accountId}
                />
            </Form.Item>
            <Form.Item
                name="startMessageTemplateId"
                label={t('Start message')}
                rules={[{ required: true }]}
                extra={languageNotice}
            >
                <TemplateSelect
                    templates={startTemplates}
                    loading={templates.isLoading}
                    disabled={!accountId}
                />
            </Form.Item>
        </>
    )
}
